import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Play } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContentI18n } from "@/context/ContentI18n";
import { usePlayer } from "@/context/PlayerContext";
import { Header } from "@/components/Header";
import { PlayerBar } from "@/components/PlayerBar";
import { ShareButton } from "@/components/ShareButton";
import { SongRow } from "@/components/SongRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CollectionView() {
  const { id } = useParams();
  const { t, lang, fontClass } = useLang();
  const { title: ctitle } = useContentI18n();
  const { current, playIndex } = usePlayer();
  const [col, setCol] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/collections/${id}`).then((r) => { if (live) setCol(r.data); })
      .catch(() => {}).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id]);

  const songs = col?.songs || [];

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)" }}>
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <Link to="/rooms/sindu-kamare" data-testid="collection-back-link" className="inline-flex items-center gap-2 font-ui font-bold text-sm" style={{ color: "var(--accent)" }}>
          <ArrowLeft size={18} /> {t("back_room")}
        </Link>
      </div>

      {loading ? (
        <div className="max-w-4xl mx-auto px-6 py-20 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("loading")}</div>
      ) : !col ? (
        <div className="max-w-4xl mx-auto px-6 py-20 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("no_songs")}</div>
      ) : (
        <>
          <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
            <div className="flex gap-5 items-center">
              <img src={col.cover_url} alt="" className="w-28 h-28 sm:w-40 sm:h-40 rounded-3xl object-cover shrink-0 shadow-lg" style={{ border: "1px solid var(--border)" }} />
              <div className="min-w-0">
                <h1 className={`${fontClass()} text-2xl sm:text-4xl font-bold leading-tight`} style={{ color: "var(--text)" }}>
                  {ctitle(col)}
                </h1>
                {(col.description_si || col.description_en) && (
                  <p className="font-ui text-sm sm:text-base mt-2" style={{ color: "var(--text-2)" }}>
                    {lang === "si" ? (col.description_si || col.description_en) : (col.description_en || col.description_si)}
                  </p>
                )}
                <button
                  onClick={() => songs.length && playIndex(0, songs)}
                  disabled={!songs.length}
                  data-testid="collection-play-all-button"
                  className="mt-4 inline-flex items-center gap-2 h-12 px-6 rounded-full font-ui text-base font-bold transition-transform active:scale-95 hover:scale-105 disabled:opacity-50"
                  style={{ background: "var(--player-bg)", color: "var(--player-accent)" }}
                >
                  <Play size={20} fill="currentColor" /> {t("col_play_all")} · {col.count} {t("col_songs")}
                </button>
                <div className="mt-3">
                  <ShareButton testid="collection-share" text={`🎶 "${ctitle(col)}" — ${t("col_title")}`} />
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-8">
            <div className="space-y-3">
              {songs.map((song, i) => (
                <SongRow key={song.id} song={song} index={i} list={songs} />
              ))}
            </div>
          </section>
        </>
      )}

      {current && <div style={{ height: 8 }} />}
      <PlayerBar />
    </div>
  );
}
